<template>
  <m-dropdown
    ref="input"
    v-model:value="showUrlDropdownInput"
    :class="['url-input', { '-wrap': wrap }, { '-input-field': !hideBorder }, { '-hide-hover': hideHover }]"
    :title="title"
    placement="onTopLeft"
    :block="fullWidth"
    :disabled="disabled || readOnly"
    :match-trigger-width="matchTriggerWidth"
    @hide="hide"
    @click.stop
  >
    <m-focusable
      class="_url-field-wrapper"
      :hide-border="hideBorder"
      :hide-hover="hideHover"
      type="clickable"
      :read-only="readOnly"
      :full-width="fullWidth"
      :m-style="mStyle"
      :small="small"
      :show-placeholder="!hidePlaceholder && url === ''"
      :placeholder="placeholder"
      :placeholder-icon="placeholderIcon"
    >
      <div class="_url-field">
        <m-link
          :class="['_url', { '-invalid': !validLink }, { '-disabled': disableLinks }]"
          :href="href"
          target="_blank"
          :underlined="validLink && !disableLinks"
          inherit-color
          :m-style="mStyle"
          :style="resolveStyles(mStyle)"
          @click="handleLinkClick"
        >
          {{ shortenedUrl }}
        </m-link>
        <m-card
          v-if="url !== '' && hideBorder"
          padding-xxxs
          border-radius="small"
          class="_actions"
        >
          <m-tooltip>
            <m-btn
              class="_btn"
              icon="copy"
              hide-border
              fab
              super-light
              xxs
              @click.stop="copyToClipboard"
            />
            <template #title>
              {{ $t('mUrlField.copyLink') }}
            </template>
          </m-tooltip>
        </m-card>
      </div>
    </m-focusable>
    <template #overlay>
      <m-card
        no-padding
        border-radius="small"
      >
        <m-input
          v-model:value="url"
          :placeholder="placeholder"
          :placeholder-icon="placeholderIcon"
          full-width
          hide-hover
          hide-placeholder
          :hide-border="hideBorder"
          auto-focus
          @keydown.enter="showUrlDropdownInput = false"
        />
      </m-card>
    </template>
  </m-dropdown>
</template>

<script setup>
import useDebounce from 'shared/composables/debounce';
import useSnackBar from '@/composables/snackbar';
import { computed, onMounted, ref, toRef, watch } from 'vue';
import { mStyleProps, resolveStyles } from 'shared/lib/m-style-props';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  ...mStyleProps,
  title: {
    type: String,
    default: '',
  },
  value: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  small: {
    type: Boolean,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  hideBorder: {
    type: Boolean,
    default: false,
  },
  hideHover: {
    type: Boolean,
    default: false,
  },
  hidePlaceholder: {
    type: Boolean,
    default: false,
  },
  placeholderIcon: {
    type: String,
    default: '',
  },
  matchTriggerWidth: {
    type: Boolean,
    default: false,
  },
  autoFocus: {
    type: Boolean,
    default: false,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  wrap: {
    type: Boolean,
    default: false,
  },
  disableLinks: {
    type: Boolean,
    default: false,
  },
});

const input = ref(null);

const url = ref(props.value);

const showUrlDropdownInput = ref(false);
const open = () => {
  showUrlDropdownInput.value = true;
};

const emit = defineEmits(['change', 'blur']);

const changeUrl = () => {
  if (url.value === props.value) {
    return;
  }
  emit('change', url.value);
};

const { debounce, cancelAll } = useDebounce();
watch(url, () => {
  debounce(changeUrl, 500);
});

const hide = () => {
  emit('blur');
  cancelAll();
  changeUrl();
};

const localValue = toRef(props, 'value');
watch(localValue, (value) => {
  url.value = value;
});

const validLink = computed(() => url.value !== '' && /^(https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/.test(url.value));
const shortenedUrl = computed(() => {
  const maxLength = 30;
  const displayUrl = url.value.replace(/^(https:\/\/)?(www\.)?/, '');
  return displayUrl.length > maxLength ? `${displayUrl.slice(0, maxLength / 2)}[...]${displayUrl.slice(-maxLength / 2)}` : displayUrl;
});
const href = computed(() => {
  if (props.disableLinks || !validLink.value) {
    return undefined;
  }

  if (url.value.startsWith('http://') || url.value.startsWith('https://')) {
    return url.value;
  }

  return `http://${url.value}`;
});

const snackbar = useSnackBar();
const { t } = useI18n();

const copyToClipboard = () => {
  navigator.clipboard.writeText(url.value ?? '');
  snackbar.success(t('success.copied'));
};

const handleLinkClick = (event) => {
  if (props.disableLinks || !validLink.value) {
    return;
  }
  event.stopPropagation();
};

onMounted(() => {
  if (props.autoFocus) {
    open();
  }
});

defineExpose({ open });
</script>

<style scoped lang="scss" type="text/scss">
.url-input {
  align-items: flex-start;
  position: relative;
  height: 100%;

  ._url-field {
    display: block;
    max-width: 100%;
    color: $secondary-color;

    ._url {
      white-space: nowrap;

      &.-invalid {
        color: $font-color-primary;
      }

      &.-disabled {
        color: $font-color-primary;
      }
    }

    ._actions {
      display: none;
      position: absolute;
      right: .4rem;
      top: 50%;
      transform: translateY(-50%);

      ._actions-inner {
        display: flex;
        align-items: center;
      }
    }
  }

  &:hover {
    ._url-field {
      ._actions {
        display: block;
      }
    }
  }

  &.-wrap {
    ._url-field-wrapper {
      align-items: flex-start;
      padding: .6rem .8rem;
    }

    &.-input-field {
      ._url-field-wrapper {
        padding: .4rem .8rem
      }
    }

    ._url-field {
      ._url {
        white-space: pre-wrap;
        word-break: break-all;
      }

      ._actions {
        top: .5rem;
        transform: translateY(0);
      }
    }
  }

  &.-hide-hover {
    ._url-field {
      padding: .3rem 0;
    }
  }
}
</style>
